/**
 * EduSaas — Entry unique du front (8.A.3).
 *
 * Module deferred : s'exécute APRÈS tous les scripts classiques (vendors,
 * pages legacy) — `window.Pages` est donc déjà rempli (F1, cadrage 8.A.0).
 *
 * Rôles :
 *  1. poser les PONTS strangler (les pages legacy consomment des globals —
 *     source unique = les modules, les globals ne sont que des alias) ;
 *  2. installer le reporter d'erreurs ;
 *  3. démarrer l'app (App.init → gardes + registre de routes → Router.init) ;
 *  4. enregistrer le service worker.
 *
 * Les ponts sont retirés en 8.Z (plus aucun écran legacy).
 */
import { attachAutosave } from './components/autosave.js';
import { attachInlineCreate } from './components/inline-create.js';
import { confirmAction } from './components/confirm.js';
import { CONFIG } from './lib/config.js';
import { esc, escapeHtml, escapeAttr, sanitizeHtml } from './lib/escape.js';
import { videoEmbedUrl } from './lib/video-embed.js';
import { timeAgo } from './lib/dates.js';
import { installErrorReporter } from './lib/error-reporter.js';
import { I18n, t, formatPrice } from './lib/i18n.js';
import { API } from './lib/api.js';
import { Router } from './router.js';
import { TabBar } from './components/tab-bar.js';
import {
  renderImageUploader,
  renderDocUploader,
  renderVideoUploader,
} from './components/uploader.js';
import { BlockEditor } from './components/block-editor.js';
import { App } from './app.js';
import { registerServiceWorker } from './sw-register.js';

// Registre des pages legacy : créé par le premier script de page (exécuté
// avant ce module) — création défensive si aucune page legacy ne reste.
window.Pages = window.Pages || {};

// ── Ponts strangler ──
window.App = App;
window.API = API;
window.Router = Router;
window.I18n = I18n;
window.CONFIG = CONFIG;
window.TabBar = TabBar;
window.BlockEditor = BlockEditor;
window.t = t;
window.esc = esc;
window.escapeHtml = escapeHtml;
window.escapeAttr = escapeAttr;
window.sanitizeHtml = sanitizeHtml;
window.videoEmbedUrl = videoEmbedUrl;
// `timeAgo` était défini par lesson.js et consommé par community.js via l'ordre
// de chargement (couplage relevé à l'inventaire 8.B.0 §2). Débrancher lesson.js
// en 8.B.4 a cassé la communauté (« timeAgo is not defined ») : le pont prend
// le relais jusqu'à la migration de cet écran (8.B.7).
window.timeAgo = timeAgo;
window.formatPrice = formatPrice;
window.renderImageUploader = renderImageUploader;
window.renderDocUploader = renderDocUploader;
window.renderVideoUploader = renderVideoUploader;
// Pont vers les écrans encore en legacy : ils bénéficient de l'enregistrement
// automatique sans attendre leur migration, et le composant leur survivra.
window.attachAutosave = attachAutosave;
window.attachInlineCreate = attachInlineCreate;
window.confirmAction = confirmAction;

installErrorReporter();

// Un module deferred s'exécute avant DOMContentLoaded ; garde readyState
// par robustesse (si le shell était un jour injecté dynamiquement).
const start = (): void => {
  void App.init();
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}

registerServiceWorker();
